import { queryOptions, useQuery } from "@tanstack/react-query";
import { restGet } from "@/lib/supabaseRest";
import { ssrMemoSwr } from "@/lib/ssrMemo";

export type HeroButton = {
  label: string;
  href: string;
  variant?: "primary" | "secondary" | "ghost";
  icon?: string;            // lucide icon name
  open_in_new_tab?: boolean;
  visible?: boolean;
};

export type HeroSlideContent = {
  id?: string;
  image_desktop?: string;
  image_mobile?: string;
  title?: string;
  subtitle?: string;
  buttons?: HeroButton[];
  fit?: "cover" | "contain";
  hide_text?: boolean;
};

export type HeroLayout = {
  autoplay?: boolean;
  interval_ms?: number;
  overlay?: "off" | "light" | "medium" | "strong";
  text_align?: "left" | "center" | "right";
  min_height_mobile_px?: number;
  min_height_desktop_px?: number;
};

export type HeroContent = {
  layout?: HeroLayout;
  slides: HeroSlideContent[];
};

/**
 * Fetch a hero content row from page_content.
 *
 *   Homepage carousel: slug = "home",   sectionKey = "hero_slides"
 *   Interior page:     slug = <slug>,   sectionKey = "page_hero"
 *
 * Returns { data, isLoading }. Callers should fall back to their hard-coded
 * defaults whenever data is null/undefined so the public site never breaks
 * if a row is missing or the fetch fails.
 */
export function heroContentQueryOptions(slug: string, sectionKey: "hero_slides" | "page_hero") {
  return queryOptions({
    queryKey: ["hero-content", slug, sectionKey],
    enabled: Boolean(slug),
    queryFn: async (): Promise<HeroContent | null> => {
      // Plain PostgREST fetch: runs on first paint, so it must not pull
      // supabase-js into the initial bundle.
      //
      // Reuse fresh public content for 60s. A warm isolate can serve its saved
      // snapshot for up to 10 minutes while a single refresh runs in background.
      // An empty/expired isolate still awaits real CMS content: no guessed slide,
      // blank first paint, or mismatch between the route preload and its banner.
      // Browsers bypass the SSR memo and retain Query's freshness/invalidation.
      return ssrMemoSwr(`hero-content:${slug}:${sectionKey}`, 60_000, 600_000, async () => {
        const rows = await restGet<{ content: unknown; is_published: boolean }[]>(
          `page_content?select=content,is_published&page_slug=eq.${encodeURIComponent(slug)}&section_key=eq.${sectionKey}&limit=1`,
        );
        // restGet returns null for transport/HTTP failures, but [] for a
        // successful missing row. Reject only failures so background outages
        // cannot replace a saved banner; missing/unpublished rows still clear it.
        if (rows === null) throw new Error("Hero content read unavailable");
        const data = rows?.[0];
        if (!data || data.is_published === false) return null;
        const c = data.content as any;
        if (!c || !Array.isArray(c.slides)) return null;
        return c as HeroContent;
      }).catch(() => null);
    },
    // SSR/loader data is fresh — don't immediately refetch the same
    // page_content row after hydration (it re-enters the LCP critical path
    // and duplicates the boot-script request). 60s staleness per tab session
    // is acceptable for CMS hero edits. SSR may supply the last saved snapshot
    // during revalidation; a subsequent request receives the refreshed value.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

export function useHeroContent(slug: string, sectionKey: "hero_slides" | "page_hero") {
  return useQuery(heroContentQueryOptions(slug, sectionKey));
}